import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { COLORS, monoLabel, monoValue } from '../../lib/theme';
import { HISTORY_WINDOW_MS, HOURLY_WINDOW_MS } from '../../hooks/useDenshiState';
import { bucketColumns, smoothColumns } from '../../lib/trace';

const GRID_PX = 64;

// Half-width of the smoothed line's moving average, in pixel columns. Seven
// columns is about a second of the 60s window at typical panel widths --
// enough to take the sensor jitter out of a 10Hz trace, not enough to flatten
// a wave-period swing.
const SMOOTH_RADIUS_PX = 3;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  background: ${COLORS.feedGround};
  border: 1px solid ${COLORS.hairline};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 6px 10px;
  background: ${COLORS.headerFill};
  border-bottom: 1px solid ${COLORS.hairline};
  flex: 0 0 auto;
`;

const HeaderLabel = styled.span`
  ${monoLabel}
  font-size: 9px;
  color: ${COLORS.label};
  white-space: nowrap;
`;

const HeaderValue = styled.span`
  ${monoValue}
  color: ${COLORS.text};
  white-space: nowrap;
`;

const Plot = styled.div`
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
`;

// HTML rather than canvas text: the labels stay crisp at any DPR without
// re-rendering them, and they can't be stretched by the plot area's sizing.
const YAxis = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  flex: 0 0 auto;
  padding: 2px 4px 2px 6px;
  border-right: 1px solid ${COLORS.hairline};
  ${monoValue}
  font-size: 9px;
  color: ${COLORS.label};
  line-height: 1;
`;

const PlotArea = styled.div`
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
`;

const Canvas = styled.canvas`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 5px 10px;
  border-top: 1px solid ${COLORS.hairline};
  flex: 0 0 auto;
`;

const FooterText = styled.span`
  ${monoValue}
  font-size: 9px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${COLORS.label};
  white-space: nowrap;
`;

// The 1-minute average is this panel's single yellow item.
const FooterAccent = styled(FooterText)`
  color: ${COLORS.accent};
`;

// The hourly buffer only fills from page load, so until a full hour has been
// seen the label says how long the mean really spans -- a five-minute mean
// must never read as an hourly one. Under a minute it adds nothing to the
// 1-minute average and is left out.
function hourlyLabel(hourly) {
  if (!hourly || hourly.mean == null || hourly.coverageMs < 60_000) return null;
  if (hourly.coverageMs >= HOURLY_WINDOW_MS) return '1 h avg';

  return `${Math.floor(hourly.coverageMs / 60_000)} m avg`;
}

// One continuous path stepping hi-then-lo through each occupied column. At
// high sample rates adjacent columns touch and this reads as the intended
// min/max spike envelope; when samples are sparser than pixel columns the
// same path stays joined instead of scattering into disconnected dots.
function drawSpikes(ctx, columns, toY) {
  ctx.beginPath();
  let started = false;
  for (let x = 0; x < columns.length; x += 1) {
    const col = columns[x];
    if (!col) continue;

    const px = x + 0.5;
    const yHi = toY(col.hi);
    const yLo = toY(col.lo);

    if (!started) {
      ctx.moveTo(px, yHi);
      started = true;
    } else {
      ctx.lineTo(px, yHi);
    }
    ctx.lineTo(px, yLo);
  }
  ctx.stroke();
}

// A polyline through the moving average of each occupied column, joined
// across empty ones for the same reason the spike path is.
function drawSmoothed(ctx, columns, toY) {
  const values = smoothColumns(columns, SMOOTH_RADIUS_PX);

  ctx.lineJoin = 'round';
  ctx.beginPath();
  let started = false;
  for (let x = 0; x < values.length; x += 1) {
    if (values[x] === undefined) continue;

    const px = x + 0.5;
    const py = toY(values[x]);
    if (!started) {
      ctx.moveTo(px, py);
      started = true;
    } else {
      ctx.lineTo(px, py);
    }
  }
  ctx.stroke();
}

/**
 * Two ways to put the same buffer on screen, chosen by `smooth`.
 *
 * Off: min/max vertical spikes, one per device pixel column -- not a
 * polyline. At 10Hz over a 60s window there are more samples than columns, so
 * a line would alias badly and hide the extremes; spikes keep every peak the
 * window actually contains.
 *
 * On: one continuous line through a light moving average of the same column
 * buckets (see lib/trace.js). It trades the guarantee above for legibility:
 * the hi-then-lo stepping reads as a sawtooth on a signal that is really
 * smooth, and for a panel that answers "what is it doing" rather than "what
 * did it peak at" the trend is the point. The y range is still the window's
 * raw min/max, so the axis labels stay honest about the extremes even when
 * the averaged line doesn't reach them.
 *
 * The canvas is sized in device pixels and the context scaled by DPR,
 * otherwise every hairline lands between pixels and blurs.
 */
function draw(canvas, points, nowMs, traceColor, smooth) {
  const ctx = canvas.getContext('2d');
  const dpr = window.devicePixelRatio || 1;
  const cssW = canvas.clientWidth;
  const cssH = canvas.clientHeight;
  if (!cssW || !cssH) return;

  canvas.width = Math.round(cssW * dpr);
  canvas.height = Math.round(cssH * dpr);
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, cssW, cssH);

  // Grid first, so the trace sits over it.
  ctx.strokeStyle = COLORS.gridLine;
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let x = GRID_PX; x < cssW; x += GRID_PX) {
    ctx.moveTo(Math.round(x) + 0.5, 0);
    ctx.lineTo(Math.round(x) + 0.5, cssH);
  }
  for (let y = GRID_PX; y < cssH; y += GRID_PX) {
    ctx.moveTo(0, Math.round(y) + 0.5);
    ctx.lineTo(cssW, Math.round(y) + 0.5);
  }
  ctx.stroke();

  if (!points || points.length < 2) return;

  const values = points.map((p) => p.v);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const tStart = nowMs - HISTORY_WINDOW_MS;
  const toY = (v) => cssH - ((v - min) / span) * cssH;

  const columns = bucketColumns(points, tStart, HISTORY_WINDOW_MS, cssW);

  ctx.strokeStyle = traceColor;
  ctx.lineWidth = 1;
  if (smooth) drawSmoothed(ctx, columns, toY);
  else drawSpikes(ctx, columns, toY);

  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  ctx.strokeStyle = COLORS.meanLine;
  ctx.setLineDash([4, 3]);
  ctx.beginPath();
  const my = Math.round(toY(mean)) + 0.5;
  ctx.moveTo(0, my);
  ctx.lineTo(cssW, my);
  ctx.stroke();
  ctx.setLineDash([]);
}

export default function TrendChart({
  label,
  points,
  unit,
  format,
  nowMs,
  traceColor,
  showAverage,
  hourly,
  smooth = false,
}) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    draw(canvas, points, nowMs, traceColor, smooth);

    const observer = new ResizeObserver(() => draw(canvas, points, nowMs, traceColor, smooth));
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [points, nowMs, traceColor, smooth]);

  const latest = points?.length ? points[points.length - 1].v : null;
  const mean = points?.length
    ? points.reduce((a, p) => a + p.v, 0) / points.length
    : null;

  // Top, middle, bottom of the same range the canvas fits the trace into --
  // the scale is window-relative, so without labels a flat trace and a wild
  // one look identical.
  const ticks = (() => {
    if (!points || points.length < 2) return [null, null, null];

    const values = points.map((p) => p.v);
    const min = Math.min(...values);
    const max = Math.max(...values);
    return [max, min + (max - min) / 2, min];
  })();

  const hourlyText = hourlyLabel(hourly);

  return (
    <Panel>
      <Header>
        <HeaderLabel>{label}</HeaderLabel>
        <HeaderValue>{latest != null ? `${format(latest)} ${unit}` : '--'}</HeaderValue>
      </Header>
      <Plot>
        <YAxis>
          {ticks.map((v, i) => (
            <span key={i}>{v == null ? '--' : format(v)}</span>
          ))}
        </YAxis>
        <PlotArea>
          <Canvas ref={canvasRef} />
        </PlotArea>
      </Plot>
      <Footer>
        {showAverage && hourlyText ? (
          <FooterText>
            {hourlyText} {format(hourly.mean)} {unit}
          </FooterText>
        ) : (
          <FooterText>{unit}</FooterText>
        )}
        {showAverage && mean != null ? (
          <FooterAccent>
            1 m avg {format(mean)} {unit}
          </FooterAccent>
        ) : (
          <FooterText>{HISTORY_WINDOW_MS / 1000} s window</FooterText>
        )}
      </Footer>
    </Panel>
  );
}
